import React from 'react';
import { Linkedin, Mail, ChevronDown, Download } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo } from '../data/mock';
import html2pdf from 'html2pdf.js';

const Hero = () => {
  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDownloadCV = async () => {
    try {
      // Fetch CV HTML
      const response = await fetch('/Ahmet_Kadayifci_CV.html');
      const htmlContent = await response.text();

      // Create temporary container
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      document.body.appendChild(tempDiv);

      // PDF options
      const opt = {
        margin: 0,
        filename: 'Ahmet_Kadayifci_CV.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        }
      };

      // Generate PDF
      await html2pdf().set(opt).from(tempDiv).save();

      // Cleanup
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
      // Fallback: open in new tab
      window.open('/Ahmet_Kadayifci_CV.html', '_blank');
    }
  };

  return (
    <section className="min-h-screen bg-black relative flex items-center overflow-hidden pt-28 md:pt-0">
      {/* Subtle grid background */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black opacity-90"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 py-8 md:py-0">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left side - Content */}
          <div className="space-y-6 md:space-y-8 order-2 md:order-1">
            <div className="space-y-3 md:space-y-4">
              <div className="inline-block">
                <span className="text-gray-400 text-xs md:text-sm font-mono tracking-wider uppercase border border-gray-700 px-3 md:px-4 py-1.5 md:py-2 rounded-full">
                  Executive Search • Human Resources
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight">
                {personalInfo.name}
              </h1>

              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-400 font-light max-w-xl">
                {personalInfo.tagline}
              </p>
            </div>

            <p className="text-gray-500 text-sm md:text-lg max-w-xl leading-relaxed">
              {personalInfo.summary}
            </p>

            {/* QR Code and CTA Buttons */}
            <div className="flex flex-row items-center gap-4 md:gap-6">
              {/* QR Code - Left side, bigger */}
              <div className="flex flex-col items-center p-3 md:p-4 bg-gray-900/50 border border-gray-800 rounded-xl">
                <div className="bg-white p-2 md:p-3 rounded-lg">
                  <img
                    src="/contact-qr.png"
                    alt="Contact QR Code"
                    className="w-20 h-20 md:w-28 md:h-28"
                  />
                </div>
                <span className="text-gray-400 text-xs mt-2">Scan for contact</span>
                <a
                  href="/ahmet-murat-kadayifci.vcf"
                  download="Ahmet_Murat_Kadayifci.vcf"
                  className="text-white text-xs hover:text-gray-300 transition-colors flex items-center gap-1 mt-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Save Contact
                </a>
              </div>

              {/* Buttons - Right side, stacked vertically */}
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => window.open(personalInfo.linkedin, '_blank')}
                  className="bg-white text-black hover:bg-gray-200 transition-all duration-300 px-6 md:px-8 py-4 md:py-5 text-sm md:text-base"
                  size="lg"
                >
                  <Linkedin className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  LinkedIn Profile
                </Button>
                <Button
                  onClick={() => window.location.href = `mailto:${personalInfo.email}`}
                  variant="outline"
                  className="border-gray-700 text-white hover:bg-gray-900 transition-all duration-300 px-6 md:px-8 py-4 md:py-5 text-sm md:text-base"
                  size="lg"
                >
                  <Mail className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  Get in Touch
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 pt-6 md:pt-8 border-t border-gray-800">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-white">8+</div>
                <div className="text-xs md:text-sm text-gray-500 mt-1">Years Exp.</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-white">50+</div>
                <div className="text-xs md:text-sm text-gray-500 mt-1">Executive Searches</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-white">15+</div>
                <div className="text-xs md:text-sm text-gray-500 mt-1">Industries</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-white">20+</div>
                <div className="text-xs md:text-sm text-gray-500 mt-1">Client Projects</div>
              </div>
            </div>
          </div>

          {/* Right side - Image */}
          <div className="relative order-1 md:order-2">
            <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-lg mx-auto">
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-48 md:w-72 h-48 md:h-72 bg-gray-800 rounded-lg opacity-50 blur-3xl"></div>
              <div className="absolute -bottom-4 -right-4 w-48 md:w-72 h-48 md:h-72 bg-gray-700 rounded-lg opacity-30 blur-3xl"></div>

              {/* Image container */}
              <div className="relative z-10 border-2 md:border-4 border-gray-800 rounded-xl md:rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={personalInfo.photo}
                  alt={personalInfo.name}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToAbout}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer"
      >
        <ChevronDown className="w-8 h-8 text-gray-600" />
      </button>
    </section>
  );
};

export default Hero;
